import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FV_MODAL_EXTERNAL_PADDING_SIZE,
  FV_MODAL_HEADER_POSITION,
  FV_MODAL_POSITION,
  FV_MODAL_SIZE,
  FvModalComponent,
} from '@discocil/fv-components';
import { TranslateModule } from '@ngx-translate/core';
import {
  filter,
  first,
  Observable,
  Subscription,
  takeUntil,
  timer,
  tap,
  switchMap,
  mergeMap
} from 'rxjs';
import { DEFAULT_CURRENCY } from 'src/app/config/app.config';
import { OrganizationRepositoryAdapter } from 'src/app/core/entities/organization/infrastructure/adapters/organization-repository.adapter';
import { IPaymentSummary } from 'src/app/core/interfaces/payment.interface';
import { PaymentsService } from 'src/app/core/services/payments/payments.service';

@Component({
  selector: 'app-change-delivered-modal',
  templateUrl: './change-delivered-modal.component.html',
  standalone: true,
  imports: [FvModalComponent, TranslateModule, CurrencyPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeDeliveredModalComponent implements OnInit {
  private readonly paymentsService = inject(PaymentsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly organizationService = inject(OrganizationRepositoryAdapter);

  @ViewChild('changeDelivered', { static: false })
  changeModal!: FvModalComponent;

  paymentResume!: IPaymentSummary | null;
  closeModalTimer$!: Observable<0>;
  closeModalTimerSubscription!: Subscription | undefined;
  MODAL_TIMER = window?.modalTimerTest ? window?.modalTimerTest : 10000;
  isModalOpen: boolean = false;
  currency: string = DEFAULT_CURRENCY;

  FV_MODAL_HEADER_POSITION = FV_MODAL_HEADER_POSITION;
  FV_MODAL_SIZE = FV_MODAL_SIZE;
  FV_MODAL_POSITION = FV_MODAL_POSITION;
  FV_MODAL_EXTERNAL_PADDING_SIZE = FV_MODAL_EXTERNAL_PADDING_SIZE;

  ngOnInit(): void {
    this.currency =
      this.organizationService.getOrganizationFromCache()?.currency ||
      DEFAULT_CURRENCY;
    this.onCashPaymentListener();
  }

  private onCashPaymentListener(): void {
    this.paymentsService.onCashPaymentDelivered$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((paymentResume: IPaymentSummary) => paymentResume.changeDelivered !== 0),
        tap((paymentResume: IPaymentSummary) => {
          this.paymentResume = paymentResume;
          this.changeModal.open();
        }),
        mergeMap(() => this.changeModal.isOpenChange.pipe(
          first(),
          tap((isOpen: boolean) => {
            this.isModalOpen = isOpen;
          })
        )),
        switchMap(() => {
          const modalCloseListener = this.changeModal.isOpenChange.pipe(
            filter((isOpen) => !isOpen)
          );
          
          return timer(this.MODAL_TIMER).pipe(
            takeUntilDestroyed(this.destroyRef),
            takeUntil(modalCloseListener)
          );
        }),
        tap(() => {
          if (this.isModalOpen) {
            this.changeModal.close();
          }

          this.paymentResume = null;
        })
      )
      .subscribe();
  }
}
